import { Prec } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";

export function buildEditorSelectionInfo(view) {
    const mainSelection = view.state.selection.main;
    const head = mainSelection.head;
    const anchor = mainSelection.anchor;
    const line = view.state.doc.lineAt(head);
    const from = Math.min(anchor, head);
    const to = Math.max(anchor, head);
    const startLine = view.state.doc.lineAt(from);
    const endLine = view.state.doc.lineAt(to);

    return {
        line: line.number,
        column: head - line.from + 1,
        from,
        to,
        hasSelection: from !== to,
        startLine: startLine.number,
        startColumn: from - startLine.from + 1,
        endLine: endLine.number,
        endColumn: to - endLine.from + 1,
        selectedText: from !== to ? view.state.sliceDoc(from, to) : "",
    };
}

function openSelectionActionMenuAtSelection(view, onOpenSelectionActionMenu) {
    const selectionInfo = buildEditorSelectionInfo(view);
    if (!selectionInfo.hasSelection) {
        onOpenSelectionActionMenu?.(null);
        return false;
    }

    const fromCoords = view.coordsAtPos(selectionInfo.from);
    const toCoords = view.coordsAtPos(selectionInfo.to);
    const fallbackCoords = view.coordsAtPos(view.state.selection.main.head);
    const anchorCoords = fromCoords || toCoords || fallbackCoords;

    if (!anchorCoords) {
        onOpenSelectionActionMenu?.(null);
        return false;
    }

    const top = Math.min(fromCoords?.top ?? anchorCoords.top, toCoords?.top ?? anchorCoords.top);
    const left =
        fromCoords && toCoords
            ? (Math.min(fromCoords.left, toCoords.left) + Math.max(fromCoords.right, toCoords.right)) / 2
            : (anchorCoords.left + anchorCoords.right) / 2;

    onOpenSelectionActionMenu?.({
        top,
        left,
        selection: selectionInfo,
    });
    return true;
}

export function selectionActionContextMenu(onOpenSelectionActionMenu) {
    if (!onOpenSelectionActionMenu) {
        return [];
    }

    let animationFrameId = null;

    const scheduleSelectionActionMenuSync = (view) => {
        if (animationFrameId != null) {
            cancelAnimationFrame(animationFrameId);
        }

        animationFrameId = requestAnimationFrame(() => {
            animationFrameId = null;
            openSelectionActionMenuAtSelection(view, onOpenSelectionActionMenu);
        });
    };

    return [
        EditorView.domEventHandlers({
            mouseup(_event, view) {
                scheduleSelectionActionMenuSync(view);
                return false;
            },
            keyup(_event, view) {
                scheduleSelectionActionMenuSync(view);
                return false;
            },
        }),
        EditorView.updateListener.of((update) => {
            if (!update.selectionSet) {
                return;
            }

            if (update.state.selection.main.empty) {
                onOpenSelectionActionMenu?.(null);
            }
        }),
        Prec.high(
            keymap.of([
                {
                    key: "Shift-F10",
                    run: (view) => openSelectionActionMenuAtSelection(view, onOpenSelectionActionMenu),
                },
                {
                    key: "ContextMenu",
                    run: (view) => openSelectionActionMenuAtSelection(view, onOpenSelectionActionMenu),
                },
            ]),
        ),
    ];
}
